import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button, Modal, Select } from '@scripsteam/scrips-signal-ds';
import { TagSearchField } from '@/settings/components/tag-search-field';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  useDegreeSearch,
  useMedicalSchoolsSearch,
} from '@/settings/services';
import type { EducationQualification } from '@/types/settings-api';

/**
 * Qualification add/edit dialog — PROD-761 commit 10.
 *
 * Flutter parity: `edit_medical_school_widget.dart`. Three fields, all
 * mandatory:
 *
 *   1. UNIVERSITY *  — typeahead (Master/MedicalSchools)
 *   2. DEGREE *      — typeahead (Master/DegreeValueSet)
 *   3. GRADUATION YEAR * — dropdown, current year ... current-100
 *
 * Footer: Save / Cancel (+ Remove only when editing existing). Same
 * button layout as Flutter dart:194-244.
 *
 * Wire-shape preserved:
 *   - issuer  / issuerName  ← MedicalSchool master (`Id`, `MedicalSchool`)
 *   - degree  / degreeName  ← Degree master (`degree`, `code.displayName`)
 *   - code                  ← Degree master `code` (full SNOMED envelope)
 *   - duration.start        — stays null (graduation year only — no start)
 *   - duration.end          — graduation year (integer)
 *   - id: null on create    — backend allocates the join PK; survives
 *     JSON.stringify (only undefined is stripped). Same invariant as
 *     Languages / Skills / Specialities / RFV typeaheads.
 *
 * The dialog owns a draft `EducationQualification` and commits changes
 * back to the parent via `onSave(next)`. Parent splices the draft into
 * `EducationWorkExperience.qualifications` and dispatches the existing
 * `useSaveEducationWorkExperience` mutation — no new mutation here.
 * Per-tab save semantics preserved.
 */
export interface QualificationEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: EducationQualification | null;
  onSave: (next: EducationQualification) => void;
  onRemove?: () => void;
}

function makeEmptyQualification(): EducationQualification {
  return {
    id: null,
    issuer: null,
    issuerName: '',
    degree: null,
    degreeName: '',
    code: {
      code: null,
      displayName: null,
      system: null,
      description: null,
      shortName: null,
    },
    duration: { start: null, end: null },
  };
}

export function QualificationEditDialog({
  open,
  onOpenChange,
  initial,
  onSave,
  onRemove,
}: QualificationEditDialogProps) {
  const isEdit = !!initial && initial.id !== null;
  const [draft, setDraft] = useState<EducationQualification>(
    initial ?? makeEmptyQualification(),
  );

  // Confirmation gate for Remove — Flutter has no confirm step, but
  // since this React surface lacks a soft-delete flag (the save endpoint
  // replaces the qualifications array, so missing = deleted), a single
  // misclick has no recovery path after the parent's "Save Educational
  // Details" round-trips. Two-tap pattern: first click → button asks
  // "Confirm Remove?", second click → actually removes. Resets when
  // the dialog closes.
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // Year dropdown bounds — computed inline on every render, NOT cached
  // at module load. Dialog stays mounted across opens (parent doesn't
  // unmount it), so a useMemo with stable deps would freeze the array
  // on first mount. Per @samer review on PR #124, a session that
  // crosses a year boundary (Dec 31 → Jan 1, open app left running)
  // should pick up the new year automatically. The array is 101 items
  // of integer literals — no allocation cost worth memoising.
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 101 }, (_, i) => currentYear - i);

  // Reset draft each time the dialog opens with a new `initial`. Don't
  // reset on close — keeps the draft alive if user accidentally clicks
  // backdrop, but the parent controls open state so this is just for
  // safety. Also resets the Remove confirmation gate on every open so
  // a previously-confirming row doesn't carry over to the next edit.
  useEffect(() => {
    if (open) {
      setDraft(initial ?? makeEmptyQualification());
      setConfirmingRemove(false);
    }
  }, [open, initial]);

  const isValid =
    !!draft.issuer &&
    !!draft.degree &&
    draft.duration.end !== null;

  const handleSave = () => {
    if (!isValid) {
      toast.error('University, degree, and graduation year are required.');
      return;
    }
    onSave(draft);
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={isEdit ? 'Edit Qualification' : 'Add Qualification'}
      size="lg"
      footer={<><div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={handleSave}
              disabled={!isValid}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
          {isEdit && onRemove && (
            <Button
              type="button"
              variant="tertiary"
              onClick={() => {
                if (confirmingRemove) {
                  onRemove();
                  onOpenChange(false);
                } else {
                  setConfirmingRemove(true);
                }
              }}
              className={
                confirmingRemove
                  ? 'bg-status-error text-text-inverse hover:bg-status-error/90'
                  : 'text-status-error hover:text-status-error hover:bg-status-error/10'
              }
            >
              {confirmingRemove ? 'Confirm Remove' : 'Remove'}
            </Button>
          )}</>}
      footerAlign="between"
    >
        

        <div data-slot="settings-dialog-body" className="space-y-4 py-2">
          <UniversityPicker
            value={{ id: draft.issuer, name: draft.issuerName }}
            onSelect={(picked) =>
              setDraft({
                ...draft,
                issuer: picked.id,
                issuerName: picked.name,
              })
            }
          />

          <DegreePicker
            value={{ id: draft.degree, name: draft.degreeName }}
            onSelect={(picked) =>
              setDraft({
                ...draft,
                degree: picked.id,
                degreeName: picked.name,
                code: picked.code,
              })
            }
          />

          <div>
            <FieldLabel stacked>
              Graduation year <span className="text-status-error">*</span>
            </FieldLabel>
            <Select
              value={draft.duration.end?.toString() ?? ''}
              onChange={(val) =>
                setDraft({
                  ...draft,
                  duration: {
                    ...draft.duration,
                    end: val ? Number(val) : null,
                  },
                })
              }
              placeholder="Select graduation year"
              options={yearOptions.map((y) => ({
                label: y.toString(),
                value: y.toString(),
              }))}
            />
          </div>
        </div>

        
      </Modal>
  );
}

function UniversityPicker({
  value,
  onSelect,
}: {
  value: { id: string | null; name: string };
  onSelect: (picked: { id: string; name: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value.name);
  const debouncedSearch = useDebounce(search, 250);
  const { data: results = [], isLoading } = useMedicalSchoolsSearch(debouncedSearch);

  const options = useMemo(
    () =>
      results.map((r) => ({
        id: r.Id,
        label: r.MedicalSchool,
      })),
    [results],
  );

  const handleSelect = (id: string) => {
    const found = results.find((r) => r.Id === id);
    if (!found) return;
    setSearch(found.MedicalSchool);
    onSelect({ id: found.Id, name: found.MedicalSchool });
    setOpen(false);
  };

  return (
    <div>
      <FieldLabel stacked>
        University <span className="text-status-error">*</span>
      </FieldLabel>
      <TagSearchField
        value={search}
        placeholder="Enter university"
        open={open}
        onOpenChange={setOpen}
        onSearchChange={setSearch}
        loading={isLoading}
        options={options}
        onSelect={handleSelect}
      />
    </div>
  );
}

function DegreePicker({
  value,
  onSelect,
}: {
  value: { id: string | null; name: string };
  onSelect: (picked: {
    id: string;
    name: string;
    code: EducationQualification['code'];
  }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value.name);
  const debouncedSearch = useDebounce(search, 250);
  const { data: results = [], isLoading } = useDegreeSearch(debouncedSearch);

  const options = useMemo(
    () =>
      results.map((r) => ({
        id: r.degree,
        label: r.code.displayName ?? r.code.code ?? r.degree,
        secondary: r.code.code ?? undefined,
      })),
    [results],
  );

  const handleSelect = (id: string) => {
    const found = results.find((r) => r.degree === id);
    if (!found) return;
    const name = found.code.displayName ?? found.code.code ?? '';
    setSearch(name);
    onSelect({ id: found.degree, name, code: found.code });
    setOpen(false);
  };

  return (
    <div>
      <FieldLabel stacked>
        Degree <span className="text-status-error">*</span>
      </FieldLabel>
      <TagSearchField
        value={search}
        placeholder="Enter degree"
        open={open}
        onOpenChange={setOpen}
        onSearchChange={setSearch}
        loading={isLoading}
        options={options}
        onSelect={handleSelect}
      />
    </div>
  );
}
